/**
 * Compute-blade registry — the paperwork & compliance "blades" of the
 * BeetleSense spider web (see docs/ARCHITECTURE_SPIDERWEB.md).
 *
 * These are document/decision generators (Skogsbruksplan drafter, §30 self-
 * check, Samråd overlap), NOT photo-scan modules. They deliberately live in a
 * SEPARATE registry from `modules/registry.ts` so they never pollute the scan
 * ModuleId union or the Forest Health Index fusion — exactly as the ratified
 * decision keeps EUDR/carbon as destinations, not scan modules
 * (MODULE_INTEGRATION.md §9a.5).
 *
 * They share the spider-web shape: each reaches its own audience at its own
 * address (`tier`/`publicDomain`), and each funnels back to the one BeetleSense
 * app. Metadata only here — no heavy imports — so any surface can read it.
 */

import {
  ClipboardList,
  ScrollText,
  ShieldAlert,
  FileCheck,
  Calculator,
  TrendingUp,
  Sprout,
  PawPrint,
  NotebookPen,
  Truck,
  Bug,
  CloudRain,
  BadgeCheck,
  Boxes,
  Ruler,
  Shovel,
  Gauge,
  Landmark,
  Cylinder,
  Focus,
  Scissors,
  Axe,
  LineChart,
  Plane,
  Route,
  Handshake,
  Wind,
  Droplets,
  Shield,
  Database,
  Flame,
  Waves,
  Mountain,
  ShieldCheck,
  Leaf,
  Trees,
  Users,
  type LucideIcon,
} from 'lucide-react'
import type { ModuleTier } from '@/services/modules/registry'

export type BladeId =
  | 'skogsbruksplan'
  | 'svl30'
  | 'heirsProperty'
  | 'disasterRelief'
  | 'samrad'
  | 'eudrDds'
  | 'eudrHs'
  | 'volume'
  | 'harvest'
  | 'regeneration'
  | 'browse'
  | 'journal'
  | 'trafficability'
  | 'pheromone'
  | 'stormClaim'
  | 'certPrep'
  | 'logScaling'
  | 'height'
  | 'planting'
  | 'siteIndex'
  | 'landValue'
  | 'sectionVolume'
  | 'relascope'
  | 'thinning'
  | 'bucking'
  | 'increment'
  | 'swarming'
  | 'rootRot'
  | 'stormRisk'
  | 'soilMoisture'
  | 'grunddata'
  | 'forestRisk'
  | 'climateStress'
  | 'carbonEstimate'
  | 'wildfireRisk'
  | 'floodRisk'
  | 'erosionRisk'
  | 'supplierCheck'
  | 'materialFlow'
  | 'shipmentGuardian'
  | 'insuranceReadiness'
  | 'carbonBreakeven'
  | 'edgeCoreHabitat'
  | 'invasiveRisk'
  | 'coopPooling'
  | 'flightPlan'
  | 'roadPlan'
  | 'burnPlan'
  | 'brokerPack'
  | 'whorlGrowth'
  | 'spacing'
  | 'fertiliser'
  | 'speciesChoice'

/**
 * The five natural groups a forest owner thinks in. Drives the category hub on
 * /tools and the "another tool in this category" cross-links.
 */
export type BladeCategory = 'compliance' | 'volume' | 'economics' | 'regeneration' | 'operations'

import type { UserRole } from '@/stores/authStore'

export interface ComputeBladeDef {
  id: BladeId
  /** Internal route this blade's tool lives at, e.g. '/tools/forest-plan'. */
  route: string
  labelEn: string
  labelSv: string
  /** One-line value prop. */
  subEn: string
  subSv: string
  icon: LucideIcon
  /** Always 'compute' — an honest "drafted from your data" label, not a measurement. */
  kind: 'compute'
  tier: ModuleTier
  /** Public domain a Vercel rewrite points at this blade's tool, if addressed. */
  publicDomain?: string
  order: number
  /** Which of the five owner-facing groups this tool belongs to. */
  category: BladeCategory
  /** IDs of tools that naturally follow or complement this one. */
  related?: BladeId[]
  /** Slug of the Understory magazine issue that best backs this tool's method
   *  with research/credibility context, if one exists (renders a "why this
   *  matters" link in BladeToolShell). Guarded against drift by
   *  bladeMagazineLinks.test.ts — must resolve to a real published issue. */
  relatedIssueSlug?: string
  /** RBAC: which roles can access this blade. 'all' (or omitted) = no restriction. */
  requiredRoles?: UserRole[] | 'all'
}

export const COMPUTE_BLADES: ComputeBladeDef[] = [
  {
    id: 'skogsbruksplan',
    route: '/tools/forest-plan',
    labelEn: 'Forest management plan',
    labelSv: 'Skogsbruksplan',
    subEn: 'Draft a 10-year stand-by-stand plan from your forest data',
    subSv: 'Få ett 10-årigt förslag bestånd för bestånd',
    icon: ClipboardList,
    kind: 'compute',
    tier: 'landing',
    publicDomain: 'forestplan.beetlesense.ai',
    order: 1,
    category: 'compliance',
    related: ['svl30', 'samrad', 'eudrDds', 'certPrep'],
    requiredRoles: ['owner'],
  },
  {
    id: 'svl30',
    route: '/tools/forestry-act-30',
    labelEn: 'Forestry Act §30 self-check',
    labelSv: 'Skogsvårdslagen §30 egenkontroll',
    subEn: 'Check environmental compliance before you fell',
    subSv: 'Kontrollera miljöhänsyn innan avverkning',
    icon: ShieldAlert,
    kind: 'compute',
    tier: 'landing',
    publicDomain: 'section30.beetlesense.ai',
    order: 2,
    category: 'compliance',
    related: ['samrad', 'eudrDds', 'skogsbruksplan', 'certPrep'],
    requiredRoles: 'all',
  },
  {
    id: 'samrad',
    route: '/tools/consultation',
    labelEn: 'Länsstyrelsen consultation',
    labelSv: 'Samråd med Länsstyrelsen',
    subEn: 'Detect Natura 2000 / key-habitat overlap and draft the request',
    subSv: 'Hitta överlapp med Natura 2000 / nyckelbiotop och förbered ansökan',
    icon: ScrollText,
    kind: 'compute',
    tier: 'landing',
    publicDomain: 'consultation.beetlesense.ai',
    order: 3,
    category: 'compliance',
    related: ['svl30', 'eudrDds', 'skogsbruksplan', 'certPrep'],
    requiredRoles: 'all',
  },
  {
    id: 'heirsProperty',
    route: '/tools/heirs-property',
    labelEn: 'Heirs-property risk check',
    labelSv: 'Arvsfastighet — riskkoll',
    subEn: 'Spot a forced-sale / tangled-title exposure on inherited land',
    subSv: 'Hitta risk för tvångsförsäljning och oklar titel på ärvd mark',
    icon: ScrollText,
    kind: 'compute',
    tier: 'landing',
    publicDomain: 'heirs.beetlesense.ai',
    order: 9,
    category: 'compliance',
    related: ['svl30', 'samrad', 'skogsbruksplan'],
    requiredRoles: 'all',
  },
  {
    id: 'disasterRelief',
    route: '/tools/disaster-relief',
    labelEn: 'Disaster-relief readiness check',
    labelSv: 'Katastrofstöd — beredskapskoll',
    subEn: 'Be positioned to claim EFRP / FSA / FEMA relief before the disaster',
    subSv: 'Var redo att söka EFRP/FSA/FEMA-stöd innan katastrofen',
    icon: FileCheck,
    kind: 'compute',
    tier: 'landing',
    publicDomain: 'relief.beetlesense.ai',
    order: 10,
    category: 'compliance',
    related: ['heirsProperty', 'svl30', 'skogsbruksplan'],
    requiredRoles: 'all',
  },
  {
    id: 'eudrDds',
    route: '/tools/eudr-dds',
    labelEn: 'EUDR due-diligence statement',
    labelSv: 'EUDR due diligence-underlag',
    subEn: 'Generate the dated deforestation-free statement for EU timber sales',
    subSv: 'Skapa det daterade avskogningsfria underlaget för EU-försäljning',
    icon: FileCheck,
    kind: 'compute',
    tier: 'landing',
    publicDomain: 'eudr.beetlesense.ai',
    order: 4,
    category: 'compliance',
    related: ['svl30', 'samrad', 'volume', 'skogsbruksplan', 'eudrHs'],
    relatedIssueSlug: 'issue-02-the-window-you-cant-see',
    requiredRoles: 'all',
  },
  {
    id: 'eudrHs',
    route: '/tools/eudr-hs',
    labelEn: 'EUDR HS code scope checker',
    labelSv: 'EUDR HS-kodskontroll',
    subEn: 'Check if your wood product HS code falls under EUDR Annex I',
    subSv: 'Kontrollera om din träprodukts HS-kod omfattas av EUDR Bilaga I',
    icon: Database,
    kind: 'compute',
    tier: 'landing',
    publicDomain: 'eudr-hs.beetlesense.ai',
    order: 29,
    category: 'compliance',
    related: ['eudrDds', 'svl30', 'samrad'],
    requiredRoles: 'all',
  },
  {
    id: 'volume',
    route: '/tools/volume',
    labelEn: 'Timber volume calculator',
    labelSv: 'Virkesvolymkalkyl',
    subEn: 'Estimate stem and standing volume with Swedish volume functions',
    subSv: 'Uppskatta stam- och beståndsvolym med svenska volymfunktioner',
    icon: Calculator,
    kind: 'compute',
    tier: 'landing',
    publicDomain: 'volume.beetlesense.ai',
    order: 5,
    category: 'volume',
    related: ['height', 'relascope', 'sectionVolume', 'harvest'],
    relatedIssueSlug: 'issue-02-the-window-you-cant-see',
    requiredRoles: 'all',
  },
  {
    id: 'harvest',
    route: '/tools/harvest-calc',
    labelEn: 'Harvest-timing calculator',
    labelSv: 'Avverkningskalkyl',
    subEn: 'Fell now or wait? A net-present-value comparison',
    subSv: 'Avverka nu eller vänta? En nuvärdesjämförelse',
    icon: TrendingUp,
    kind: 'compute',
    tier: 'landing',
    publicDomain: 'harvest.beetlesense.ai',
    order: 6,
    category: 'economics',
    related: ['volume', 'landValue', 'increment', 'bucking'],
    requiredRoles: 'all',
  },
  {
    id: 'regeneration',
    route: '/tools/regeneration',
    labelEn: 'Regeneration survey',
    labelSv: 'Föryngringsinventering',
    subEn: 'Count seedlings in sample plots → stems/ha vs target',
    subSv: 'Räkna plantor i provytor → plantor/ha mot mål',
    icon: Sprout,
    kind: 'compute',
    tier: 'landing',
    publicDomain: 'regeneration.beetlesense.ai',
    order: 7,
    category: 'regeneration',
    related: ['planting', 'browse', 'siteIndex', 'skogsbruksplan'],
    requiredRoles: 'all',
  },
  {
    id: 'browse',
    route: '/tools/browse-damage',
    labelEn: 'Browse-damage survey',
    labelSv: 'Viltbetesinventering',
    subEn: 'Estimate annual moose/deer browse damage on young pine',
    subSv: 'Uppskatta årlig viltbetesskada på ungtall',
    icon: PawPrint,
    kind: 'compute',
    tier: 'landing',
    publicDomain: 'browse.beetlesense.ai',
    order: 8,
    category: 'regeneration',
    related: ['regeneration', 'planting', 'skogsbruksplan'],
    requiredRoles: 'all',
  },
  {
    id: 'journal',
    route: '/tools/field-journal',
    labelEn: 'Field journal',
    labelSv: 'Skogsdagbok',
    subEn: 'Log field observations — auto-sorted by type',
    subSv: 'Logga fältobservationer — sorteras automatiskt',
    icon: NotebookPen,
    kind: 'compute',
    tier: 'landing',
    publicDomain: 'journal.beetlesense.ai',
    order: 9,
    category: 'operations',
    related: ['pheromone', 'trafficability', 'skogsbruksplan'],
    requiredRoles: 'all',
  },
  {
    id: 'trafficability',
    route: '/tools/trafficability',
    labelEn: 'Trafficability planner',
    labelSv: 'Körbarhet & markskoning',
    subEn: 'Can the harvester drive in without rutting?',
    subSv: 'Går marken att köra på utan spårbildning?',
    icon: Truck,
    kind: 'compute',
    tier: 'landing',
    publicDomain: 'trafficability.beetlesense.ai',
    order: 10,
    category: 'operations',
    related: ['journal', 'stormClaim', 'skogsbruksplan'],
    requiredRoles: 'all',
  },
  {
    id: 'flightPlan',
    route: '/tools/flight-plan',
    labelEn: 'Drone flight planner',
    labelSv: 'Drönarflygplan',
    subEn: 'Grid-scan mission from your parcel — altitude, photos, batteries',
    subSv: 'Rutnätsuppdrag från din fastighet — höjd, foton, batterier',
    icon: Plane,
    kind: 'compute',
    tier: 'landing',
    publicDomain: 'flightplan.beetlesense.ai',
    order: 44,
    category: 'operations',
    related: ['trafficability', 'journal', 'grunddata'],
    requiredRoles: 'all',
  },
  {
    id: 'roadPlan',
    route: '/tools/road-plan',
    labelEn: 'Forest road cost',
    labelSv: 'Skogsbilväg — kostnad',
    subEn: 'Itemised build cost for a gravel forest road',
    subSv: 'Byggkostnad post för post för en skogsbilväg',
    icon: Route,
    kind: 'compute',
    tier: 'landing',
    publicDomain: 'skogsbilvag.beetlesense.ai',
    order: 45,
    category: 'operations',
    related: ['trafficability', 'harvest', 'journal'],
    requiredRoles: 'all',
  },
  {
    id: 'burnPlan',
    route: '/tools/burn-plan',
    labelEn: 'Prescribed burn planner',
    labelSv: 'Naturvårdsbränning',
    subEn: 'Is the burn window safe? Go / caution / no-go + checklist',
    subSv: 'Är brännfönstret säkert? Klart/förbehåll/avrådan + checklista',
    icon: Flame,
    kind: 'compute',
    tier: 'landing',
    publicDomain: 'branning.beetlesense.ai',
    order: 46,
    category: 'operations',
    related: ['wildfireRisk', 'regeneration', 'journal'],
    requiredRoles: 'all',
  },
  {
    id: 'brokerPack',
    route: '/tools/broker-pack',
    labelEn: 'Broker pack',
    labelSv: 'Mäklarpaket',
    subEn: 'Indicative estate valuation + buyer/EUDR dossier for a sale',
    subSv: 'Vägledande värdering + köpar-/EUDR-underlag inför försäljning',
    icon: Handshake,
    kind: 'compute',
    tier: 'landing',
    publicDomain: 'maklarpaket.beetlesense.ai',
    order: 47,
    category: 'economics',
    related: ['landValue', 'harvest', 'volume'],
    requiredRoles: 'all',
  },
  {
    id: 'whorlGrowth',
    route: '/tools/whorl-growth',
    labelEn: 'Growth from branch whorls',
    labelSv: 'Tillväxt från grenvarv',
    subEn: 'Read annual height growth from whorl spacing on young conifers',
    subSv: 'Läs årlig höjdtillväxt från grenvarvens avstånd på ung barrskog',
    icon: Sprout,
    kind: 'compute',
    tier: 'landing',
    publicDomain: 'grenvarv.beetlesense.ai',
    order: 48,
    category: 'volume',
    related: ['increment', 'height', 'siteIndex'],
    requiredRoles: 'all',
  },
  {
    id: 'spacing',
    route: '/tools/spacing',
    labelEn: 'Spacing & density check',
    labelSv: 'Förband & täthet',
    subEn: 'Are stems too close? Relative spacing + thinning need',
    subSv: 'Står stammarna för tätt? Relativt förband + röjningsbehov',
    icon: Trees,
    kind: 'compute',
    tier: 'landing',
    publicDomain: 'stamtathet.beetlesense.ai',
    order: 49,
    category: 'regeneration',
    related: ['planting', 'thinning', 'regeneration'],
    requiredRoles: 'all',
  },
  {
    id: 'fertiliser',
    route: '/tools/fertiliser',
    labelEn: 'Forest fertilisation',
    labelSv: 'Skogsgödsling',
    subEn: 'Nitrogen dose, cost and payback before a drone/aerial spread',
    subSv: 'Kvävegiva, kostnad och lönsamhet inför drönar-/flygspridning',
    icon: Droplets,
    kind: 'compute',
    tier: 'landing',
    publicDomain: 'godsling.beetlesense.ai',
    order: 50,
    category: 'economics',
    related: ['harvest', 'siteIndex', 'increment'],
    requiredRoles: 'all',
  },
  {
    id: 'speciesChoice',
    route: '/tools/species-choice',
    labelEn: 'Species choice by site',
    labelSv: 'Trädslagsval efter mark',
    subEn: 'What to plant after felling — right tree, right site',
    subSv: 'Vad du ska plantera efter avverkning — rätt träd på rätt mark',
    icon: Sprout,
    kind: 'compute',
    tier: 'landing',
    publicDomain: 'tradval.beetlesense.ai',
    order: 51,
    category: 'regeneration',
    related: ['regeneration', 'planting', 'siteIndex'],
    requiredRoles: 'all',
  },
  {
    id: 'pheromone',
    route: '/tools/pheromone-trap',
    labelEn: 'Pheromone-trap log',
    labelSv: 'Feromonfälla-logg',
    subEn: 'Log bark-beetle trap catches and spot swarming risk',
    subSv: 'Logga barkborrefångster och se svärmningsrisk',
    icon: Bug,
    kind: 'compute',
    tier: 'landing',
    publicDomain: 'pheromone.beetlesense.ai',
    order: 11,
    category: 'operations',
    related: ['swarming', 'journal', 'stormClaim', 'svl30'],
    relatedIssueSlug: 'issue-07-how-a-sensor-learns-to-smell',
    requiredRoles: 'all',
  },
  {
    id: 'stormClaim',
    route: '/tools/storm-damage',
    labelEn: 'Storm-insurance dossier',
    labelSv: 'Stormskade-dossier',
    subEn: 'Compile a storm-damage claim packet for your insurer',
    subSv: 'Sammanställ försäkringsunderlag efter storm',
    icon: CloudRain,
    kind: 'compute',
    tier: 'landing',
    publicDomain: 'stormclaim.beetlesense.ai',
    order: 12,
    category: 'operations',
    related: ['svl30', 'volume', 'journal', 'skogsbruksplan'],
    requiredRoles: 'all',
  },
  {
    id: 'certPrep',
    route: '/tools/certification',
    labelEn: 'FSC/PEFC audit prep',
    labelSv: 'Certifieringsrevision (FSC/PEFC)',
    subEn: 'Check readiness before a certification audit',
    subSv: 'Kontrollera läget inför certifieringsrevision',
    icon: BadgeCheck,
    kind: 'compute',
    tier: 'landing',
    publicDomain: 'certification.beetlesense.ai',
    order: 13,
    category: 'compliance',
    related: ['svl30', 'samrad', 'skogsbruksplan', 'eudrDds'],
    requiredRoles: 'all',
  },
  {
    id: 'logScaling',
    route: '/tools/log-pile',
    labelEn: 'Timber-pile volume',
    labelSv: 'Timmerstapel — volym',
    subEn: 'Estimate a roadside log pile’s volume from its dimensions',
    subSv: 'Uppskatta en vältas volym från måtten vid bilväg',
    icon: Boxes,
    kind: 'compute',
    tier: 'landing',
    publicDomain: 'logscaling.beetlesense.ai',
    order: 14,
    category: 'volume',
    related: ['sectionVolume', 'bucking', 'volume'],
    requiredRoles: 'all',
  },
  {
    id: 'height',
    route: '/tools/tree-height',
    labelEn: 'Tree-height calculator',
    labelSv: 'Trädhöjdsmätare',
    subEn: 'Tree height from distance and clinometer angles',
    subSv: 'Trädhöjd från avstånd och vinklar (klinometer)',
    icon: Ruler,
    kind: 'compute',
    tier: 'landing',
    publicDomain: 'height.beetlesense.ai',
    order: 15,
    category: 'volume',
    related: ['volume', 'siteIndex', 'sectionVolume'],
    requiredRoles: 'all',
  },
  {
    id: 'planting',
    route: '/tools/planting',
    labelEn: 'Planting calculator',
    labelSv: 'Planteringskalkyl',
    subEn: 'Spacing → stems/ha, seedlings to order and cost',
    subSv: 'Förband → plantor/ha, beställning och kostnad',
    icon: Shovel,
    kind: 'compute',
    tier: 'landing',
    publicDomain: 'planting.beetlesense.ai',
    order: 16,
    category: 'regeneration',
    related: ['regeneration', 'browse', 'siteIndex'],
    requiredRoles: 'all',
  },
  {
    id: 'siteIndex',
    route: '/tools/site-index',
    labelEn: 'Site index (bonitet)',
    labelSv: 'Ståndortsindex (bonitet)',
    subEn: 'Indicative site index from age and dominant height',
    subSv: 'Indikativ bonitet från ålder och övre höjd',
    icon: Gauge,
    kind: 'compute',
    tier: 'landing',
    publicDomain: 'siteindex.beetlesense.ai',
    order: 17,
    category: 'economics',
    related: ['height', 'increment', 'landValue'],
    requiredRoles: 'all',
  },
  {
    id: 'landValue',
    route: '/tools/land-value',
    labelEn: 'Land value (Faustmann)',
    labelSv: 'Markvärde (Faustmann)',
    subEn: 'Bare-land value and the optimal rotation',
    subSv: 'Markvärde och ekonomiskt optimal omloppstid',
    icon: Landmark,
    kind: 'compute',
    tier: 'landing',
    publicDomain: 'landvalue.beetlesense.ai',
    order: 18,
    category: 'economics',
    related: ['harvest', 'increment', 'siteIndex'],
    requiredRoles: 'all',
  },
  {
    id: 'sectionVolume',
    route: '/tools/section-volume',
    labelEn: 'Log volume (sections)',
    labelSv: 'Sektionskubering',
    subEn: 'Cube a log with Huber, Smalian and Newton',
    subSv: 'Kubera en stock med Huber, Smalian och Newton',
    icon: Cylinder,
    kind: 'compute',
    tier: 'landing',
    publicDomain: 'sectionvolume.beetlesense.ai',
    order: 19,
    category: 'volume',
    related: ['logScaling', 'bucking', 'volume'],
    requiredRoles: 'all',
  },
  {
    id: 'relascope',
    route: '/tools/relascope',
    labelEn: 'Basal area (relascope)',
    labelSv: 'Grundyta (relaskop)',
    subEn: 'Basal area per hectare from an angle count',
    subSv: 'Grundyta per hektar från relaskopräkning',
    icon: Focus,
    kind: 'compute',
    tier: 'landing',
    // The relascope.beetlesense.ai door belongs to the standalone optical
    // micro-app (scan-module registry); this in-suite basal-area calculator
    // gets its own address so it doesn't collide with it.
    publicDomain: 'basalarea.beetlesense.ai',
    order: 20,
    category: 'volume',
    related: ['thinning', 'volume', 'increment'],
    requiredRoles: 'all',
  },
  {
    id: 'thinning',
    route: '/tools/thinning',
    labelEn: 'Thinning calculator',
    labelSv: 'Gallringskalkyl',
    subEn: 'Removed and residual basal area and yield',
    subSv: 'Uttag och kvarvarande grundyta samt volym',
    icon: Scissors,
    kind: 'compute',
    tier: 'landing',
    publicDomain: 'thinning.beetlesense.ai',
    order: 21,
    category: 'economics',
    related: ['relascope', 'volume', 'harvest', 'siteIndex'],
    requiredRoles: 'all',
  },
  {
    id: 'bucking',
    route: '/tools/bucking',
    labelEn: 'Bucking optimizer',
    labelSv: 'Apteringskalkyl',
    subEn: 'Cross-cut a stem into the best-paying assortments',
    subSv: 'Aptera en stam till bäst betalande sortiment',
    icon: Axe,
    kind: 'compute',
    tier: 'landing',
    publicDomain: 'bucking.beetlesense.ai',
    order: 22,
    category: 'economics',
    related: ['sectionVolume', 'logScaling', 'harvest'],
    requiredRoles: 'all',
  },
  {
    id: 'increment',
    route: '/tools/growth',
    labelEn: 'Increment (MAI/CAI)',
    labelSv: 'Tillväxtkalkyl (MAI/CAI)',
    subEn: 'Mean vs current increment and biological rotation',
    subSv: 'Medel- mot löpande tillväxt och biologisk omlopp',
    icon: LineChart,
    kind: 'compute',
    tier: 'landing',
    publicDomain: 'increment.beetlesense.ai',
    order: 23,
    category: 'economics',
    related: ['siteIndex', 'landValue', 'harvest', 'thinning'],
    requiredRoles: 'all',
  },
  {
    id: 'swarming',
    route: '/tools/swarming-forecast',
    labelEn: 'Bark-beetle swarming forecast',
    labelSv: 'Granbarkborrens svärmningsprognos',
    subEn: "SLU's degree-day model: spring onset + next-gen ready",
    subSv: 'SLU:s temperatursummemodell: vårstart + nästa generation',
    icon: Bug,
    kind: 'compute',
    tier: 'landing',
    publicDomain: 'swarming.beetlesense.ai',
    order: 24,
    category: 'operations',
    related: ['pheromone', 'journal', 'stormClaim'],
    relatedIssueSlug: 'issue-05-the-beetle-moving-north',
  },
  {
    id: 'rootRot',
    route: '/tools/root-rot',
    labelEn: 'Root-rot stump-treatment decision',
    labelSv: 'Rotröta — stubbehandlingsbeslut',
    subEn: 'Rotstop vs urea by temperature — SLU research',
    subSv: 'Rotstop eller urea utifrån temperatur — SLU-forskning',
    icon: Shield,
    kind: 'compute',
    tier: 'landing',
    publicDomain: 'rootrot.beetlesense.ai',
    order: 25,
    category: 'operations',
    related: ['harvest', 'thinning', 'journal'],
  },
  {
    id: 'stormRisk',
    route: '/tools/storm-risk',
    labelEn: 'Stand-level storm risk',
    labelSv: 'Stormrisk för bestånd',
    subEn: 'Persson + Blennow pre-screen for Heureka WINDA',
    subSv: 'Persson + Blennow — pre-screen för Heureka WINDA',
    icon: Wind,
    kind: 'compute',
    tier: 'landing',
    publicDomain: 'stormrisk.beetlesense.ai',
    order: 26,
    category: 'operations',
    related: ['stormClaim', 'trafficability', 'harvest'],
  },
  {
    id: 'soilMoisture',
    route: '/tools/soil-moisture',
    labelEn: 'Soil moisture & rutting risk',
    labelSv: 'Markfuktighet & körskaderisk',
    subEn: "SLU's soil-moisture map + GYL bearing capacity",
    subSv: 'SLU:s markfuktighetskarta + GYL-bärighet',
    icon: Droplets,
    kind: 'compute',
    tier: 'landing',
    publicDomain: 'moisture.beetlesense.ai',
    order: 27,
    category: 'operations',
    related: ['trafficability', 'stormRisk', 'harvest'],
  },
  {
    id: 'grunddata',
    route: '/tools/forest-data',
    labelEn: 'Skogliga grunddata lookup',
    labelSv: 'Skogliga grunddata — uppslagning',
    subEn: 'Laser-derived volume/height/basal-area for a polygon',
    subSv: 'Laser-härledd volym/höjd/grundyta för en polygon',
    icon: Database,
    kind: 'compute',
    tier: 'landing',
    publicDomain: 'grunddata.beetlesense.ai',
    order: 28,
    category: 'volume',
    related: ['volume', 'siteIndex', 'skogsbruksplan', 'height'],
  },
  // ── Risk hub — imported from gravity-gummifabriken (PestEarlyWarning deduped
  //    against the existing pheromone + swarming blades). ──────────────────────
  {
    id: 'forestRisk',
    route: '/tools/forest-risk',
    labelEn: 'Forest risk dashboard',
    labelSv: 'Skogsriskpanel',
    subEn: 'Composite bark-beetle + storm risk from spruce share and history',
    subSv: 'Samlad barkborre- och stormrisk från granandel och historik',
    icon: Gauge,
    kind: 'compute',
    tier: 'landing',
    publicDomain: 'forestrisk.beetlesense.ai',
    order: 30,
    category: 'operations',
    related: ['swarming', 'stormRisk', 'rootRot'],
  },
  {
    id: 'climateStress',
    route: '/tools/climate-stress',
    labelEn: 'Climate stress test',
    labelSv: 'Klimatstresstest',
    subEn: 'Indicative stress level for a stand under a warmer, drier climate',
    subSv: 'Vägledande stressnivå för ett bestånd i ett varmare, torrare klimat',
    icon: CloudRain,
    kind: 'compute',
    tier: 'landing',
    publicDomain: 'climatestress.beetlesense.ai',
    order: 31,
    category: 'operations',
    related: ['stormRisk', 'swarming', 'siteIndex'],
  },
  {
    id: 'carbonEstimate',
    route: '/tools/carbon',
    labelEn: 'Carbon sequestration',
    labelSv: 'Kolinlagring',
    subEn: 'Estimate your forest’s annual CO₂ capture from area and increment',
    subSv: 'Uppskatta skogens årliga koldioxidupptag från areal och tillväxt',
    icon: Sprout,
    kind: 'compute',
    tier: 'landing',
    publicDomain: 'carbon.beetlesense.ai',
    order: 32,
    category: 'economics',
    related: ['volume', 'increment', 'eudrDds'],
  },
  // ── Environmental & climate hazards (from the Forest Manager problems doc).
  //    Wildfire / flood / erosion are net-new: not covered by stormRisk (wind),
  //    soilMoisture (machine rutting) or climateStress (drought/heat). ─────────
  {
    id: 'wildfireRisk',
    route: '/tools/wildfire-risk',
    labelEn: 'Wildfire risk index',
    labelSv: 'Brandriskindex',
    subEn: 'Indicative wildfire risk from conifer share, dryness and ignition sources',
    subSv: 'Vägledande brandrisk från barrandel, torrhet och tändkällor',
    icon: Flame,
    kind: 'compute',
    tier: 'landing',
    publicDomain: 'wildfire.beetlesense.ai',
    order: 33,
    category: 'operations',
    related: ['climateStress', 'forestRisk', 'stormRisk'],
  },
  {
    id: 'floodRisk',
    route: '/tools/flood-risk',
    labelEn: 'Flood risk',
    labelSv: 'Översvämningsrisk',
    subEn: 'Indicative flood risk from proximity to water, low ground and drainage',
    subSv: 'Vägledande översvämningsrisk från vattennärhet, lågt läge och dränering',
    icon: Waves,
    kind: 'compute',
    tier: 'landing',
    publicDomain: 'flood.beetlesense.ai',
    order: 34,
    category: 'operations',
    related: ['soilMoisture', 'trafficability', 'climateStress'],
  },
  {
    id: 'erosionRisk',
    route: '/tools/erosion-risk',
    labelEn: 'Erosion & landslide risk',
    labelSv: 'Erosions- och rasrisk',
    subEn: 'Indicative erosion/landslide risk from slope, soil and clear-felling',
    subSv: 'Vägledande erosions-/rasrisk från lutning, jordart och slutavverkning',
    icon: Mountain,
    kind: 'compute',
    tier: 'landing',
    publicDomain: 'erosion.beetlesense.ai',
    order: 35,
    category: 'operations',
    related: ['soilMoisture', 'trafficability', 'stormRisk'],
  },
  // ── Supply chain & chain of custody (problems doc §3). Distinct, non-
  //    overlapping tools; the immutable traceability *ledger* is an app feature,
  //    so its CoC logic ships once here as the material-flow mass balance. ──────
  {
    id: 'supplierCheck',
    route: '/tools/supplier-check',
    labelEn: 'Supplier verification',
    labelSv: 'Leverantörskontroll',
    subEn: 'EUDR readiness of your supplier base from geolocation + legality coverage',
    subSv: 'Leverantörskedjans EUDR-beredskap från geolokalisering + laglighet',
    icon: BadgeCheck,
    kind: 'compute',
    tier: 'landing',
    publicDomain: 'suppliers.beetlesense.ai',
    order: 36,
    category: 'compliance',
    related: ['eudrDds', 'certPrep', 'eudrHs'],
  },
  {
    id: 'materialFlow',
    route: '/tools/material-flow',
    labelEn: 'Material flow & chain of custody',
    labelSv: 'Materialflöde & spårbarhet',
    subEn: 'Mass-balance integrity when known and unknown wood are mixed',
    subSv: 'Massbalans när känt och okänt virke blandas',
    icon: Boxes,
    kind: 'compute',
    tier: 'landing',
    publicDomain: 'materialflow.beetlesense.ai',
    order: 37,
    category: 'compliance',
    related: ['eudrDds', 'supplierCheck', 'certPrep'],
  },
  {
    id: 'shipmentGuardian',
    route: '/tools/shipment-guardian',
    labelEn: 'Shipment guardian',
    labelSv: 'Transportvakt',
    subEn: 'Pre-customs document check so a missing paper cannot halt the load',
    subSv: 'Dokumentkontroll innan tull så ett saknat papper inte stoppar lasten',
    icon: Truck,
    kind: 'compute',
    tier: 'landing',
    publicDomain: 'shipment.beetlesense.ai',
    order: 38,
    category: 'compliance',
    related: ['eudrHs', 'eudrDds', 'certPrep'],
  },
  // ── Owner-resilience tools (smallholder / LCFE problems brief): risk transfer,
  //    market access and ecological quality at the family-forest scale. Net-new:
  //    insurability ≠ wildfire risk; carbon break-even ≠ carbon capture; core
  //    habitat and biosecurity are not covered by any existing blade. ──────────
  {
    id: 'insuranceReadiness',
    route: '/tools/insurance-readiness',
    labelEn: 'Wildfire insurance readiness',
    labelSv: 'Brandförsäkring — beredskapskoll',
    subEn: 'Score the wildfire mitigation insurers look for before they non-renew',
    subSv: 'Bedöm brandskyddet en försäkringsgivare tittar på innan de säger upp',
    icon: ShieldCheck,
    kind: 'compute',
    tier: 'landing',
    publicDomain: 'insurance.beetlesense.ai',
    order: 39,
    category: 'operations',
    related: ['wildfireRisk', 'climateStress', 'stormClaim'],
  },
  {
    id: 'carbonBreakeven',
    route: '/tools/carbon-breakeven',
    labelEn: 'Carbon break-even',
    labelSv: 'Kolkredit — lönsamhetströskel',
    subEn: 'See whether a carbon scheme pays at your scale after its fixed costs',
    subSv: 'Se om ett kolprojekt lönar sig vid din skala efter de fasta kostnaderna',
    icon: Leaf,
    kind: 'compute',
    tier: 'landing',
    publicDomain: 'carboncredit.beetlesense.ai',
    order: 40,
    category: 'economics',
    related: ['carbonEstimate', 'certPrep', 'landValue'],
  },
  {
    id: 'edgeCoreHabitat',
    route: '/tools/habitat',
    labelEn: 'Edge & core habitat',
    labelSv: 'Kant- och kärnhabitat',
    subEn: 'Estimate true interior habitat after edge effects from area, shape and depth',
    subSv: 'Uppskatta äkta kärnhabitat efter kanteffekter från areal, form och djup',
    icon: Trees,
    kind: 'compute',
    tier: 'landing',
    publicDomain: 'habitat.beetlesense.ai',
    order: 41,
    category: 'operations',
    related: ['samrad', 'certPrep', 'regeneration'],
  },
  {
    id: 'invasiveRisk',
    route: '/tools/invasive-species',
    labelEn: 'Invasive-species risk check',
    labelSv: 'Invasiva arter — riskkoll',
    subEn: 'Close the pathways invasive pests, pathogens and plants use to establish',
    subSv: 'Stäng spridningsvägarna som invasiva skadegörare och växter använder',
    icon: Bug,
    kind: 'compute',
    tier: 'landing',
    publicDomain: 'invasive.beetlesense.ai',
    order: 42,
    category: 'operations',
    related: ['forestRisk', 'swarming', 'samrad'],
  },
  {
    id: 'coopPooling',
    route: '/tools/co-op-pooling',
    labelEn: 'Co-op pooling',
    labelSv: 'Virkespool — samverkanskalkyl',
    subEn: 'Net gain from pooling timber volume with neighbours vs selling alone',
    subSv: 'Nettovinst av att poola virkesvolym med grannar mot att sälja ensam',
    icon: Users,
    kind: 'compute',
    tier: 'landing',
    publicDomain: 'pooling.beetlesense.ai',
    order: 43,
    category: 'economics',
    related: ['harvest', 'landValue', 'volume'],
  },
]

export function getBlade(id: BladeId): ComputeBladeDef | undefined {
  return COMPUTE_BLADES.find((b) => b.id === id)
}

/* ------------------------------------------------------------------ *
 * Categories — the five owner-facing groups for the /tools hub.
 * ------------------------------------------------------------------ */

export interface BladeCategoryDef {
  id: BladeCategory
  labelEn: string
  labelSv: string
  /** One-line "what this group is for". */
  subEn: string
  subSv: string
  /** The tool to recommend first to someone new to the group. */
  startHere: BladeId
  order: number
}

export const BLADE_CATEGORIES: BladeCategoryDef[] = [
  {
    id: 'compliance',
    labelEn: 'Compliance & paperwork',
    labelSv: 'Regelverk & pappersarbete',
    subEn: 'Plans, self-checks and statements the authorities and buyers ask for',
    subSv: 'Planer, egenkontroller och underlag som myndigheter och köpare vill ha',
    startHere: 'skogsbruksplan',
    order: 1,
  },
  {
    id: 'volume',
    labelEn: 'Volume & measurement',
    labelSv: 'Volym & mätning',
    subEn: 'Turn field measurements into stem, stand and log volumes',
    subSv: 'Gör fältmått till stam-, bestånds- och stockvolym',
    startHere: 'volume',
    order: 2,
  },
  {
    id: 'economics',
    labelEn: 'Economics & planning',
    labelSv: 'Ekonomi & planering',
    subEn: 'When to fell, what the land is worth and how to thin',
    subSv: 'När du ska avverka, vad marken är värd och hur du gallrar',
    startHere: 'harvest',
    order: 3,
  },
  {
    id: 'regeneration',
    labelEn: 'Regeneration & damage',
    labelSv: 'Föryngring & skador',
    subEn: 'Planting, seedling survival and game browse',
    subSv: 'Plantering, plantöverlevnad och viltbete',
    startHere: 'planting',
    order: 4,
  },
  {
    id: 'operations',
    labelEn: 'Operations & monitoring',
    labelSv: 'Drift & övervakning',
    subEn: 'Logging the field, planning machines and tracking pests & storms',
    subSv: 'Logga fältet, planera maskiner och följ skadegörare & storm',
    startHere: 'journal',
    order: 5,
  },
]

export function getCategory(id: BladeCategory): BladeCategoryDef | undefined {
  return BLADE_CATEGORIES.find((c) => c.id === id)
}

/** All blades in a category, ordered, optionally excluding one id. */
export function getBladesByCategory(category: BladeCategory, exclude?: BladeId): ComputeBladeDef[] {
  return COMPUTE_BLADES.filter((b) => b.category === category && b.id !== exclude).sort(
    (a, b) => a.order - b.order,
  )
}

/* ------------------------------------------------------------------ *
 * Workflows — named multi-tool chains that complete a whole job. Drive
 * the "Step 2 of 5" progress bar and the post-result "what next" arrow.
 * ------------------------------------------------------------------ */

export type WorkflowId = 'clearcut' | 'newPlanting' | 'thinning' | 'certification' | 'stormEvent' | 'beetleMonitoring'

export interface WorkflowDef {
  id: WorkflowId
  labelEn: string
  labelSv: string
  /** Ordered tool steps. The user walks them left to right. */
  steps: BladeId[]
}

export const BLADE_WORKFLOWS: WorkflowDef[] = [
  {
    id: 'clearcut',
    labelEn: 'Clear-cut prep',
    labelSv: 'Förbered slutavverkning',
    steps: ['height', 'volume', 'svl30', 'eudrDds', 'harvest'],
  },
  {
    id: 'newPlanting',
    labelEn: 'New planting',
    labelSv: 'Ny plantering',
    steps: ['planting', 'regeneration', 'browse', 'skogsbruksplan'],
  },
  {
    id: 'thinning',
    labelEn: 'Thinning job',
    labelSv: 'Gallringsjobb',
    steps: ['relascope', 'thinning', 'volume', 'harvest'],
  },
  {
    id: 'certification',
    labelEn: 'Certification',
    labelSv: 'Certifiering',
    steps: ['svl30', 'samrad', 'skogsbruksplan', 'certPrep'],
  },
  {
    id: 'stormEvent',
    labelEn: 'Storm event',
    labelSv: 'Stormhändelse',
    steps: ['stormRisk', 'stormClaim', 'volume', 'svl30'],
  },
  {
    id: 'beetleMonitoring',
    labelEn: 'Beetle monitoring workflow',
    labelSv: 'Barkborrescanning',
    steps: ['swarming', 'pheromone', 'rootRot', 'harvest', 'svl30'],
  },
]

export function getWorkflow(id: WorkflowId): WorkflowDef | undefined {
  return BLADE_WORKFLOWS.find((w) => w.id === id)
}

/** Workflows that contain a given blade, with that blade's 0-based step index. */
export function getWorkflowsForBlade(id: BladeId): { workflow: WorkflowDef; index: number }[] {
  return BLADE_WORKFLOWS.flatMap((w) => {
    const index = w.steps.indexOf(id)
    return index >= 0 ? [{ workflow: w, index }] : []
  })
}

/** The next tool in a workflow after the given blade, if any. */
export function nextInWorkflow(workflow: WorkflowDef, current: BladeId): BladeId | undefined {
  const i = workflow.steps.indexOf(current)
  return i >= 0 && i < workflow.steps.length - 1 ? workflow.steps[i + 1] : undefined
}

/** Blades reachable at their own public address (for the domain-rewrite layer). */
export const ADDRESSED_BLADES = COMPUTE_BLADES.filter((b) => !!b.publicDomain)

/** Look up a blade by the public domain a visitor arrived on. */
export function getBladeByDomain(domain: string): ComputeBladeDef | undefined {
  const host = domain.toLowerCase().replace(/^www\./, '')
  return COMPUTE_BLADES.find((b) => b.publicDomain?.toLowerCase() === host)
}

/** Look up a blade by its internal /tools route (for per-tool analytics). */
export function getBladeByRoute(route: string): ComputeBladeDef | undefined {
  return COMPUTE_BLADES.find((b) => b.route === route)
}

/* ------------------------------------------------------------------ *
 * Campaign bundles — clusters of tools by job-to-be-done, used for
 * the GTM tribal "door" hooks and persona-based marketing pages.
 * ------------------------------------------------------------------ */

export type CampaignBundleId =
  | 'harvest-decision'
  | 'compliance-eudr'
  | 'storm-response'
  | 'spring-beetle'
  | 'hunt-season'
  | 'new-owner'
  | 'biodiversity'

export interface CampaignBundle {
  id: CampaignBundleId
  labelEn: string
  labelSv: string
  taglineEn: string
  taglineSv: string
  /** Primary personas this bundle targets (A=traditional, B=urban, C=commercial, D=hunters, E=contractors, F=inspectors, G=biodiversity) */
  personas: string[]
  tools: BladeId[]
  icon: LucideIcon
  /** Optional trigger — 'storm' | 'spring' | 'hunt' | 'eudr' | undefined (evergreen) */
  trigger?: string
}

export const CAMPAIGN_BUNDLES: CampaignBundle[] = [
  {
    id: 'harvest-decision',
    labelEn: 'Harvest decision',
    labelSv: 'Avverkningsbeslut',
    taglineEn: 'From "is it worth it?" to "sign the contract" — four free reads.',
    taglineSv: 'Från "är det värt det?" till "skriv under" — fyra gratis kalkyler.',
    personas: ['C', 'E', 'A'],
    tools: ['grunddata', 'volume', 'harvest', 'logScaling', 'trafficability'],
    icon: TrendingUp,
  },
  {
    id: 'compliance-eudr',
    labelEn: 'Compliance & EUDR 2026',
    labelSv: 'Regelefterlevnad & EUDR 2026',
    taglineEn: 'Everything your timber buyer and the EU will ask for — free to prepare.',
    taglineSv: 'Allt din virkesköpare och EU kräver — gratis att förbereda.',
    personas: ['B', 'C', 'F'],
    tools: ['eudrDds', 'svl30', 'samrad', 'certPrep', 'skogsbruksplan'],
    icon: FileCheck,
    trigger: 'eudr',
  },
  {
    id: 'storm-response',
    labelEn: 'Storm response',
    labelSv: 'Stormrespons',
    taglineEn: 'Scope, claim, clear — in the hours that matter.',
    taglineSv: 'Kartlägg, anmäl, röj — på de timmar som räknas.',
    personas: ['B', 'C', 'E', 'A'],
    tools: ['stormRisk', 'stormClaim', 'soilMoisture', 'trafficability', 'volume'],
    icon: CloudRain,
    trigger: 'storm',
  },
  {
    id: 'spring-beetle',
    labelEn: 'Spring beetle watch',
    labelSv: 'Vårens barkborreberedskap',
    taglineEn: 'Catch it in the green-attack window, log the traps, time the salvage.',
    taglineSv: 'Fånga det i gröna anfallsstadiet, logga fällorna, timing för salvage.',
    personas: ['A', 'C', 'F'],
    tools: ['swarming', 'pheromone', 'rootRot', 'harvest', 'svl30'],
    icon: Bug,
    trigger: 'spring',
  },
  {
    id: 'hunt-season',
    labelEn: 'Hunt season',
    labelSv: 'Jaktsäsongen',
    taglineEn: 'Count the game, measure the browse, log it — from your phone.',
    taglineSv: 'Räkna viltet, mät betestrycket, logga det — från telefonen.',
    personas: ['D', 'A'],
    tools: ['browse', 'journal'],
    icon: PawPrint,
    trigger: 'hunt',
  },
  {
    id: 'new-owner',
    labelEn: 'New forest owner',
    labelSv: 'Ny skogsägare',
    taglineEn: 'Just inherited a forest? Start here, free, no procurement.',
    taglineSv: 'Ärvde en skog nyligen? Börja här, gratis, inget avtal.',
    personas: ['B'],
    tools: ['skogsbruksplan', 'relascope', 'volume', 'svl30'],
    icon: Sprout,
  },
  {
    id: 'biodiversity',
    labelEn: 'Biodiversity',
    labelSv: 'Biologisk mångfald',
    taglineEn: 'Manage what you can hear and see; prove it for FSC/PEFC.',
    taglineSv: 'Förvalta det du kan höra och se; bevisa det för FSC/PEFC.',
    personas: ['G'],
    tools: ['samrad', 'regeneration', 'certPrep'],
    icon: Sprout,
  },
]

export function getCampaignBundle(id: CampaignBundleId): CampaignBundle | undefined {
  return CAMPAIGN_BUNDLES.find((b) => b.id === id)
}
